import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";

const partners = [
  {
    name: "Trimble",
    product: "Tekla Structures",
    body: "Authorized user of Trimble's flagship BIM platform for steel detailing, connection modeling and constructible design.",
  },
  {
    name: "Design Data",
    product: "SDS2",
    body: "Genuine SDS2 subscriptions for automatic connection design, clash detection and fabrication-ready output.",
  },
];

export const WorkPlatform = () => (
  <section
    id="work-platform"
    className="relative py-20 md:py-28 border-t border-slate-200/60 bg-slate-50/60"
  >
    <div className="px-6 md:px-10 lg:px-14 xl:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
            <span className="w-8 h-px bg-[#0A2540]" />
            05 · Work platform
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight leading-[1.1]">
            Every software
            <br />
            we use is{" "}
            <span className="text-slate-400">Genuine</span>.
          </h2>
        </div>
        <div className="lg:col-span-5 flex lg:items-end">
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
            We work closely with{" "}
            <span className="text-[#0A2540] font-semibold">Trimble</span> and{" "}
            <span className="text-[#0A2540] font-semibold">Design Data</span>{" "}
            to fight piracy. Every seat is genuine, every project is
            traceable, and every drawing you receive is delivered on genuine,
            up-to-date tools.
          </p>
        </div>
      </div>

      {/* Partner cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {partners.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            data-testid={`platform-card-${i}`}
            className="group relative rounded-2xl border border-slate-200 bg-white p-8 md:p-10 hover:border-[#0A2540] hover:shadow-xl hover:shadow-slate-200/60 transition-all"
          >
            <div className="flex items-start justify-between gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-[#0A2540] flex items-center justify-center group-hover:bg-[#0A2540] transition-colors">
                <BadgeCheck className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200">
                <span className="w-1.5 h-1.5 rounded-full bg-[#083785]" />
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#083785] font-semibold">
                  Genuine
                </span>
              </div>
            </div>

            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-2">
              Authorized partner
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold text-[#0A2540] leading-none tracking-tight">
              {p.name}
            </h3>
            <div className="mt-2 text-sm font-mono uppercase tracking-[0.18em] text-[#0A2540]">
              {p.product}
            </div>
            <p className="mt-5 text-sm md:text-base text-slate-600 leading-relaxed">
              {p.body}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Software logos strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12 p-8 md:p-10 rounded-2xl bg-white border border-slate-200"
      >
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-slate-500 sm:border-r sm:border-slate-200 sm:pr-10 whitespace-nowrap text-center sm:text-left">
          Software
          <br />
          we run on
        </div>
        <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/j9viyusx_image.png"
            alt="Tekla Structures and SDS2 — software partners"
            className="h-16 md:h-24 w-auto object-contain"
          />
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/f3i5ajs4_image.png"
            alt="Bluebeam Revu — reviewing partner"
            className="h-16 md:h-24 w-auto object-contain"
          />
        </div>
      </motion.div>
    </div>
  </section>
);
